"use client";

import { useEffect, useState, useMemo, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { AuthContext } from "@/hooks/auth/useAuth";
import { AuthState, AuthUser, User } from "@/types/auth";
import { UserAccountWithAccount } from "@/types/accounts";
import { useRouter } from "next/navigation";

const createUserObject = async (user: User): Promise<AuthUser> => {
  let accounts: UserAccountWithAccount[] = [];

  try {
    const { data: userAccountsData, error: userAccountsError } = await supabase
      .from("user_accounts")
      .select("*, accounts!inner(id, name, slug, is_active)")
      .eq("user_id", user.id);

    if (userAccountsError) {
      console.error("❌ Error fetching user accounts:", userAccountsError);
      throw userAccountsError;
    }

    accounts = userAccountsData || [];

    return {
      id: user.id,
      email: user.email || "",
      name:
        user.user_metadata.display_name ||
        `${user.user_metadata.first_name || ""} ${user.user_metadata.last_name || ""}`.trim() ||
        user.email ||
        "Unknown User",
      phone: user.phone || "",
      user_metadata: user.user_metadata,
      accounts,
    };
  } catch (error) {
    console.error("❌ Error fetching user accounts:", error);
    return {
      id: user.id,
      email: user.email || "",
      name:
        user.user_metadata.display_name ||
        `${user.user_metadata.first_name || ""} ${user.user_metadata.last_name || ""}`.trim() ||
        user.email ||
        "Unknown User",
      phone: user.phone || "",
      user_metadata: user.user_metadata,
      accounts,
    };
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    loading: true,
    isAuthenticated: false,
  });

  const router = useRouter();

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        setAuthState((prev) => ({ ...prev, loading: true }));

        // Get initial session
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (session?.user) {
          const user = await createUserObject(session.user);
          setAuthState({ user, loading: false, isAuthenticated: true });
        } else {
          setAuthState({ user: null, loading: false, isAuthenticated: false });
        }
      } catch (error) {
        console.error("❌ Auth initialization error:", error);
        setAuthState({ user: null, loading: false, isAuthenticated: false });
      }
    };

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("🔄 Auth state changed:", event);

      try {
        if (event === "SIGNED_IN" && session?.user) {
          setTimeout(async () => {
            const user = await createUserObject(session.user);
            setAuthState({ user, loading: false, isAuthenticated: true });
          }, 0);
        } else if (event === "SIGNED_OUT") {
          setAuthState({ user: null, loading: false, isAuthenticated: false });
          // Immediately redirect to login
          router.push("/login");
        } else if (event === "USER_UPDATED" && session) {
          setTimeout(async () => {
            console.log("update user session");
            const user = await createUserObject(session.user);
            setAuthState({ user, loading: false, isAuthenticated: true });
          }, 0);
        }
      } catch (error) {
        console.error("❌ Error handling auth state change:", error);
        setAuthState((prev) => ({ ...prev, loading: false }));
      }
    });

    initAuth();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<{ error?: string }> => {
    setAuthState((prev) => ({ ...prev, loading: true }));
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      setAuthState((prev) => ({ ...prev, loading: false }));
      return { error: error.message };
    }

    // Success - user will be set by the auth state change listener
    return {};
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    // User will be cleared and redirect will happen in auth state change listener
  }, []);

  const updateUser = useCallback(async (first_name: string, last_name: string) => {
    const { error } = await supabase.auth.updateUser({
      data: {
        first_name,
        last_name,
        display_name: `${first_name} ${last_name}`.trim(),
      },
    });

    if (error) {
      console.error("❌ Error updating user metadata:", error);
      throw error;
    }
  }, []);

  const contextValue = useMemo(
    () => ({
      user: authState.user,
      loading: authState.loading,
      isAuthenticated: authState.isAuthenticated,
      signIn,
      signOut,
      updateUser,
    }),
    [authState, signIn, signOut, updateUser]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
